/**
 * Contexto de pantalla para el chat (spec aiChatBot 2026-10-16). La ruta sale
 * sola del router (todas las páginas); las páginas ricas publican su estado con
 * useChatPageContext. ChatContext pide un snapshot en el momento del envío.
 * Va ARRIBA de ChatProvider (App.tsx) para que el chat lo pueda leer.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { buildUiContextSnapshot, getPosicionContext, type PublishedPageContext, type UiContextPayload } from '../services/chat/uiContext';
import { resolvePaginaWeb } from './chatUi/webPageTitles';

interface ChatUIContextValue {
  getSnapshot: () => Promise<UiContextPayload | undefined>;
  publish: (owner: symbol, ctx: PublishedPageContext | null) => void;
}

const Ctx = createContext<ChatUIContextValue>({ getSnapshot: async () => undefined, publish: () => {} });

export function ChatUIContextProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { activeEmpresaId } = useAuth();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const empresaRef = useRef(activeEmpresaId);
  empresaRef.current = activeEmpresaId;
  // Última publicación gana (una página rica activa a la vez; owner = instancia del hook).
  const published = useRef<Map<symbol, PublishedPageContext>>(new Map());

  const publish = useCallback((owner: symbol, ctx: PublishedPageContext | null) => {
    if (ctx) published.current.set(owner, ctx); else published.current.delete(owner);
  }, []);

  const getSnapshot = useCallback(async () => {
    const merged: PublishedPageContext = Object.assign({}, ...published.current.values());
    const posicion = await getPosicionContext(empresaRef.current ?? null).catch(() => null);
    return buildUiContextSnapshot({ pagina: resolvePaginaWeb(pathRef.current), published: merged, posicion });
  }, []);

  const value = useMemo(() => ({ getSnapshot, publish }), [getSnapshot, publish]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useChatUIContext = () => useContext(Ctx);

/** Publica el estado de la página para el chat; se limpia al desmontar o al pasar null. */
export function useChatPageContext(ctx: PublishedPageContext | null) {
  const { publish } = useContext(Ctx);
  const owner = useRef(Symbol('chat-page-ctx')).current;
  useEffect(() => { publish(owner, ctx); }, [publish, owner, ctx]);
  useEffect(() => () => publish(owner, null), [publish, owner]);
}
